import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Footer from "./Footer.js";
import anime from "animejs/lib/anime.es.js";
import guide from "./logos/guide.mp4";
import { ReactComponent as CapIcon } from "./logos/cap.svg";
import { ReactComponent as MindIcon } from "./logos/mind.svg";
import { ReactComponent as HourglassIcon } from "./logos/hourglass.svg";

export default function Home() {
  useEffect(() => {
    // Initial animation for non-icon elements
    anime({
      targets: ".home-content, .divider-h",
      translateY: [200, 0],
      opacity: [0, 1],
      duration: 1000,
      loop: false,
      delay: anime.stagger(200),
      easing: "easeOutExpo",
    });

    // Observe all elements with the 'homepage-icon' class
    const icons = document.querySelectorAll(".homepage-icon");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Animate the specific element that comes into view
            anime({
              targets: entry.target,
              borderRadius: ["100%", "5%"],
              padding: ["30px", "50px"],
              duration: 3000,
              easing: "easeOutExpo",
            });
            observer.unobserve(entry.target); // Stop observing after animation
          }
        });
      },
      { threshold: 1.0 } // Trigger when 10% of the element is visible
    );

    // Observe each icon
    icons.forEach((icon) => observer.observe(icon));

    // Cleanup observer on component unmount
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <div id="main">
        <div id="free-review-container">
          <p>Your first Literature Review is Free! See pricing details <Link to="/pricing"><span className="info-link">here</span></Link></p>
        </div>
        <div id="home-container">
          <h2>
            Your Literature Review <span id="home-title">Automated</span>
          </h2>
          <div id="home-animation">
            <div className="home-content">
              <div className="hc-tile small">
                <h3>Why Lit Pro?</h3>
                <p>
                  Lit Pro uses cutting-edge machine learning techniques to create robust, academic literature reviews that would make your advisors proud.
                </p>
                <p>
                  Simply upload the articles you would like to include in your literature review and let Lit Pro do its work!
                </p>
              </div>
              <div className="hc-tile big" id="guide-vid">
                <video width="100%" height="100%" autoPlay muted loop playsInline>
                  <source src={guide} type="video/mp4" />
                  Your browser does not support this video.
                </video>
              </div>
            </div>
            <div className="divider-h"></div>
            <div className="home-content">
              <div className="hc-tile small">
                <MindIcon alt="Brain Icon" className="homepage-icon" aria-hidden="true" />
              </div>
              <div className="hc-tile big">
                <h3>How it works</h3>
                <p>
                  Lit Pro leverages multiple Machine Learning technologies and processes to extract relevant information from academic articles, preprocess and classify the texts, summarize the texts, and create a final literature review that fits perfectly into your research goals.
                </p>
              </div>
            </div>
            <div className="divider-h"></div>
            <div className="home-content">
              <div className="hc-tile big">
                <h3>Saving you time</h3>
                <p>
                  Let's be honest, writing literature reviews is a time-consuming process. You have done your research and are ready to offer your new insights to your field &ndash; but &ndash; you must first take the time to write a complete overview of relevant literature. With Lit Pro, you can breeze through your literature review and focus your time on your novel ideas. 
                </p>
              </div>
              <div className="hc-tile small">
                <HourglassIcon alt="Hourglass Icon" className="homepage-icon" aria-hidden="true" />
              </div>
            </div>
            <div className="divider-h"></div>
            <div className="home-content">
              <div className="hc-tile small">
                <CapIcon alt="Graduation Cap Icon" className="homepage-icon" aria-hidden="true" />
              </div>
              <div className="hc-tile big">
                <h3>Rigorous Output</h3>
                <p>
                  Lit Pro utilizes custom processes and large language models to ensure only the highest quality literature reviews are created. To learn more about the technical aspects of Lit Pro, <Link to="/about"><span className="info-link">visit here!</span></Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
